import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import GuidedLearningPanel from '../../components/education/ContextualLearningPanel';
import LearningInvitationLink from '../../components/education/LearningInvitationLink';
import QuestionHelpButton from '../../components/education/QuestionHelpButton';

const getQuestionKey = (question) => question.question_key || question.key;
const getTemplateVariableKey = (question) => question.template_variable_key || getQuestionKey(question);

const getQuestionOptions = (question) => {
  if (Array.isArray(question.options_json)) return question.options_json;
  if (Array.isArray(question.options_json?.options)) return question.options_json.options;
  return [];
};

const getOptionValue = (option) => {
  if (typeof option === 'string') return option;
  return option?.value ?? option?.label ?? '';
};

const getOptionLabel = (option) => {
  if (typeof option === 'string') return option;
  return option?.label ?? option?.value ?? '';
};

const isEmptyResponse = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every((item) => {
      if (!item || typeof item !== 'object') return isEmptyResponse(item);
      return Object.entries(item)
        .filter(([key]) => key !== 'relationship')
        .every(([, fieldValue]) => isEmptyResponse(fieldValue));
    });
  }
  return value === undefined || value === null || value === '';
};

const cleanText = (value) => String(value || '').trim();

const normalizeMiddleInitial = (value) => cleanText(value).charAt(0).toUpperCase();

const composeName = (parts) => {
  const firstName = cleanText(parts?.first_name || parts?.firstName);
  const middleInitial = normalizeMiddleInitial(parts?.middle_initial || parts?.middleInitial);
  const lastName = cleanText(parts?.last_name || parts?.lastName);
  return [firstName, middleInitial, lastName].filter(Boolean).join(' ');
};

const isClientVisibleQuestion = (question) => question.client_visible !== false;

const isQuestionVisible = (question, responses, questions) => {
  if (!isClientVisibleQuestion(question)) return false;

  const logic = question.conditional_logic_json;
  if (!logic || Object.keys(logic).length === 0) return true;

  const conditions = Array.isArray(logic.conditions)
    ? logic.conditions
    : logic.question_key
      ? [logic]
      : [];

  if (conditions.length === 0) return true;

  const matchCondition = (condition) => {
    const parent = questions.find((item) => getQuestionKey(item) === condition.question_key);
    if (!parent) return true;

    const actualValue = responses[parent.id];
    const expectedValue = condition.value ?? condition.equals;

    switch (condition.operator || 'equals') {
      case 'not_equals':
        return actualValue !== expectedValue;
      case 'nonempty':
        return !isEmptyResponse(actualValue);
      case 'includes':
        return Array.isArray(actualValue)
          ? actualValue.includes(expectedValue)
          : String(actualValue || '').includes(String(expectedValue || ''));
      case 'equals':
      default:
        return actualValue === expectedValue;
    }
  };

  return logic.match === 'any'
    ? conditions.some(matchCondition)
    : conditions.every(matchCondition);
};

const ESTATE_DECISION_COLUMNS = new Set([
  'testator_name',
  'testator_first_name',
  'testator_middle_initial',
  'testator_last_name',
  'county',
  'will_document_type',
  'spouse_name',
  'spouse_first_name',
  'spouse_middle_initial',
  'spouse_last_name',
  'has_living_trust',
  'uses_pour_over_will',
  'trust_name',
  'trust_date',
  'trust_type',
  'family_structure',
  'marital_status',
  'has_children',
  'children',
  'child_count',
  'has_minor_children',
  'future_children_possible',
  'blended_family',
  'spouse_has_children',
  'final_arrangement_preference',
  'burial_location',
  'anatomical_gift_preference',
  'funeral_representative_name',
  'includes_cremation_clause',
  'debts_taxes_paid_from_residue',
  'taxes_apportioned_among_beneficiaries',
  'trust_assets_may_be_used_for_expenses',
  'has_specific_gifts',
  'specific_gifts',
  'specific_gift_alternate_plan',
  'tangible_personal_property_plan',
  'uses_tangible_property_written_statement',
  'pours_tangible_property_to_trust',
  'residue_distribution_plan',
  'residue_named_beneficiaries',
  'residue_distribution_notes',
  'residue_to_spouse',
  'residue_to_spouse_then_descendants',
  'residue_to_descendants',
  'residue_includes_stepchildren',
  'residue_pours_to_trust',
  'uses_per_capita_distribution',
  'uses_per_stirpes_distribution',
  'payments_to_minors_plan',
  'uses_testamentary_trust_for_children',
  'debt_forgiveness',
  'personal_representative_primary',
  'personal_representative_alternate',
  'guardian_primary',
  'guardian_alternate',
  'uses_joint_guardians',
  'conservator_primary',
  'conservator_alternate',
  'successor_trustee',
  'includes_personal_representative_powers',
  'includes_fiduciary_incapacity_provision',
  'includes_fiduciary_compensation_provision',
  'includes_common_disaster_provision',
  'includes_no_power_of_appointment_exercise',
  'waives_supervised_probate',
  'includes_no_contest_clause',
  'survival_period_days',
  'adopted_children_treatment',
  'stepchildren_treatment',
  'includes_afterborn_children',
  'disinherits_state_default_takers',
  'includes_statutory_benefits',
  'includes_gender_headings_signature_clauses',
  'spouse_will_uses_reciprocal_provisions',
  'spouse_will_differences',
  'client_has_patient_advocate',
  'spouse_has_patient_advocate',
  'patient_advocate_primary',
  'patient_advocate_secondary',
  'patient_advocate_additional',
  'patient_advocate_can_make_medical_decisions',
  'patient_advocate_can_authorize_mental_health_treatment',
  'patient_advocate_can_make_anatomical_gift',
  'living_will_preference',
  'artificial_nutrition_hydration_preference',
  'patient_advocate_guardian_nomination',
  'client_has_durable_power_of_attorney',
  'spouse_has_durable_power_of_attorney',
  'financial_power_of_attorney_primary',
  'financial_power_of_attorney_secondary',
  'financial_power_of_attorney_additional',
  'power_of_attorney_authority_timing',
  'power_of_attorney_court_fiduciary_nomination',
  'client_has_hipaa_authorization',
  'spouse_has_hipaa_authorization',
  'client_birthday',
  'spouse_birthday',
  'hipaa_authorizes_all_patient_advocates',
  'hipaa_authorized_individuals',
  'estate_plan_summary',
  'will_type',
  'cover_letter_with_drafts',
  'handbill_or_recording_cost_info',
  'date_needed_by',
  'living_trust_checklist_required',
  'email_drafts_to',
  'mail_drafts_to',
  'additional_notes'
]);

const INTAKE_TO_DECISION_KEYS = {
  client_full_legal_name: 'testator_name',
  client_date_of_birth: 'client_birthday',
  client_marital_status: 'marital_status',
  spouse_or_partner_name: 'spouse_name',
  has_children: 'has_children',
  blended_family: 'blended_family',
  has_existing_trust: 'has_living_trust',
  preferred_executor: 'personal_representative_primary',
  preferred_trustee: 'successor_trustee',
  preferred_financial_agent: 'financial_power_of_attorney_primary',
  preferred_healthcare_agent: 'patient_advocate_primary',
  minor_child_guardian: 'guardian_primary'
};

const BOOLEAN_DECISION_COLUMNS = new Set([
  'has_living_trust',
  'uses_pour_over_will',
  'has_children',
  'has_minor_children',
  'future_children_possible',
  'blended_family',
  'spouse_has_children',
  'includes_cremation_clause',
  'debts_taxes_paid_from_residue',
  'taxes_apportioned_among_beneficiaries',
  'trust_assets_may_be_used_for_expenses',
  'has_specific_gifts',
  'uses_tangible_property_written_statement',
  'pours_tangible_property_to_trust',
  'residue_to_spouse',
  'residue_to_spouse_then_descendants',
  'residue_to_descendants',
  'residue_includes_stepchildren',
  'residue_pours_to_trust',
  'uses_per_capita_distribution',
  'uses_per_stirpes_distribution',
  'uses_testamentary_trust_for_children',
  'uses_joint_guardians',
  'includes_personal_representative_powers',
  'includes_fiduciary_incapacity_provision',
  'includes_fiduciary_compensation_provision',
  'includes_common_disaster_provision',
  'includes_no_power_of_appointment_exercise',
  'waives_supervised_probate',
  'includes_no_contest_clause',
  'includes_afterborn_children',
  'disinherits_state_default_takers',
  'includes_statutory_benefits',
  'includes_gender_headings_signature_clauses',
  'spouse_will_uses_reciprocal_provisions',
  'client_has_patient_advocate',
  'spouse_has_patient_advocate',
  'patient_advocate_can_make_medical_decisions',
  'patient_advocate_can_authorize_mental_health_treatment',
  'patient_advocate_can_make_anatomical_gift',
  'client_has_durable_power_of_attorney',
  'spouse_has_durable_power_of_attorney',
  'client_has_hipaa_authorization',
  'spouse_has_hipaa_authorization',
  'hipaa_authorizes_all_patient_advocates',
  'cover_letter_with_drafts',
  'handbill_or_recording_cost_info',
  'living_trust_checklist_required'
]);

const NUMBER_DECISION_COLUMNS = new Set(['child_count', 'survival_period_days']);
const ARRAY_PERSON_DECISION_COLUMNS = new Set(['children']);
const ARRAY_TEXT_DECISION_COLUMNS = new Set([
  'specific_gifts',
  'residue_named_beneficiaries',
  'debt_forgiveness',
  'patient_advocate_additional',
  'financial_power_of_attorney_additional',
  'hipaa_authorized_individuals'
]);

const normalizeBoolean = (value) => {
  if (value === true || value === false) return value;
  if (value === 'Yes' || value === 'yes' || value === 'true') return true;
  if (value === 'No' || value === 'no' || value === 'false') return false;
  return null;
};

const normalizeArrayText = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((description) => ({ description }));
};

const normalizePersonRecord = (person) => {
  if (!person || typeof person !== 'object') return null;

  const firstName = cleanText(person.first_name || person.firstName);
  const middleInitial = normalizeMiddleInitial(person.middle_initial || person.middleInitial);
  const lastName = cleanText(person.last_name || person.lastName);
  const dateOfBirth = cleanText(person.date_of_birth || person.dateOfBirth);
  const relationship = cleanText(person.relationship);

  if (!firstName && !middleInitial && !lastName && !dateOfBirth) return null;

  return {
    first_name: firstName,
    middle_initial: middleInitial,
    last_name: lastName,
    full_name: composeName({ first_name: firstName, middle_initial: middleInitial, last_name: lastName }),
    date_of_birth: dateOfBirth || null,
    relationship: relationship || 'child'
  };
};

const normalizePersonArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map(normalizePersonRecord).filter(Boolean);
};

const normalizeDecisionValue = (key, value) => {
  if (isEmptyResponse(value)) return undefined;
  if (BOOLEAN_DECISION_COLUMNS.has(key)) return normalizeBoolean(value);
  if (NUMBER_DECISION_COLUMNS.has(key)) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (ARRAY_PERSON_DECISION_COLUMNS.has(key)) return normalizePersonArray(value);
  if (ARRAY_TEXT_DECISION_COLUMNS.has(key)) return normalizeArrayText(value);
  return value;
};

const IntakePage = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const autosaveReadyRef = useRef(false);
  const lastAutosaveKeyRef = useRef('');
  const questionRefsRef = useRef({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorQuestionIds, setErrorQuestionIds] = useState([]);
  
  const [matter, setMatter] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [educationByQuestion, setEducationByQuestion] = useState({});
  const [responses, setResponses] = useState({});
  const [progress, setProgress] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const isClient = profile?.role === 'client';
  const backPath = isClient ? '/dashboard' : `/matters/${matterId}`;

  // Fetch initial data
  useEffect(() => {
    const fetchIntakeData = async () => {
      if (!profile?.advisor_firm_id || !matterId) return;

      try {
        autosaveReadyRef.current = false;
        lastAutosaveKeyRef.current = '';
        setLoading(true);
        setError(null);
        setErrorQuestionIds([]);

        // 1. Fetch Matter & Verify Access
        const { data: matterData, error: matterErr } = await supabase
          .from('matters')
          .select('*, client:clients(*)')
          .eq('id', matterId)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .single();

        if (matterErr || !matterData) throw new Error("Plan not found or access denied.");
        setMatter(matterData);

        // 2. Fetch Sections
        const { data: sectionsData, error: sectionsErr } = await supabase
          .from('intake_sections')
          .select('*')
          .order('sort_order', { ascending: true });

        if (sectionsErr) throw sectionsErr;
        setSections(sectionsData || []);

        // 3. Fetch All Questions
        const { data: questionsData, error: questionsErr } = await supabase
          .from('intake_questions')
          .select('*')
          .order('sort_order', { ascending: true });

        if (questionsErr) throw questionsErr;
        setQuestions(questionsData || []);

        const { data: educationLinks, error: educationErr } = await supabase
          .from('education_question_topics')
          .select(`
            question_key,
            context_label,
            immediate_context,
            show_help_cta,
            help_cta_label,
            display_priority,
            topic:education_topics(
              id,
              title,
              slug,
              short_summary,
              why_this_matters,
              why_we_are_asking,
              plain_english_explanation,
              common_questions_json,
              common_misconceptions_json,
              examples_json,
              advisor_discussion_questions_json,
              attorney_discussion_questions_json,
              display_priority
            )
          `)
          .eq('is_active', true)
          .order('display_priority', { ascending: true });

        if (educationErr) {
          console.warn('Unable to load contextual learning topics:', educationErr);
          setEducationByQuestion({});
        } else {
          const educationMap = {};
          (educationLinks || []).forEach((link) => {
            if (!link.question_key || !link.topic) return;
            educationMap[link.question_key] = [
              ...(educationMap[link.question_key] || []),
              link
            ];
          });
          setEducationByQuestion(educationMap);
        }

        // 4. Fetch Progress and Estate Plan Decisions
        const [{ data: progressData }, { data: estatePlanDecisionData }] = await Promise.all([
          supabase
            .from('matter_intake_progress')
            .select('*')
            .eq('matter_id', matterId)
            .single(),
          supabase
            .from('estate_plan_decisions')
            .select('*')
            .eq('matter_id', matterId)
            .maybeSingle()
        ]);
        
        setProgress(progressData);
        
        // Determine starting section
        if (progressData?.current_section_id && sectionsData.length > 0) {
          const idx = sectionsData.findIndex(s => s.id === progressData.current_section_id);
          if (idx !== -1) setCurrentSectionIndex(idx);
        }

        // 5. Fetch Existing Responses
        const { data: responsesData, error: respErr } = await supabase
          .from('matter_intake_responses')
          .select('question_id, response_json')
          .eq('matter_id', matterId);

        if (respErr) throw respErr;
        
        const responseMap = {};
        responsesData?.forEach(r => {
          responseMap[r.question_id] = r.response_json?.value;
        });

        const responseByKey = {};
        questionsData?.forEach((question) => {
          const questionKey = getQuestionKey(question);
          const savedValue = responseMap[question.id];
          if (questionKey && !isEmptyResponse(savedValue)) {
            responseByKey[questionKey] = savedValue;
          }
        });

        questionsData?.forEach((question) => {
          if (!isEmptyResponse(responseMap[question.id])) return;

          const questionKey = getQuestionKey(question);
          const decisionKey = ESTATE_DECISION_COLUMNS.has(questionKey)
            ? questionKey
            : INTAKE_TO_DECISION_KEYS[questionKey];
          const decisionValue = decisionKey ? estatePlanDecisionData?.[decisionKey] : undefined;
          const fallbackValue = responseByKey[questionKey];

          if (!isEmptyResponse(decisionValue)) {
            if (ARRAY_PERSON_DECISION_COLUMNS.has(decisionKey) && Array.isArray(decisionValue)) {
              responseMap[question.id] = normalizePersonArray(decisionValue);
            } else if (ARRAY_TEXT_DECISION_COLUMNS.has(decisionKey) && Array.isArray(decisionValue)) {
              responseMap[question.id] = decisionValue
                .map((item) => item?.description || item?.name || String(item))
                .join('\n');
            } else if (BOOLEAN_DECISION_COLUMNS.has(decisionKey)) {
              responseMap[question.id] = decisionValue ? 'Yes' : 'No';
            } else {
              responseMap[question.id] = decisionValue;
            }
          } else if (!isEmptyResponse(fallbackValue)) {
            responseMap[question.id] = fallbackValue;
          }
        });

        questionsData?.forEach((question) => {
          const questionKey = getQuestionKey(question);
          if (!isEmptyResponse(responseMap[question.id])) return;

          if (questionKey === 'testator_first_name') responseMap[question.id] = matterData.client?.first_name || '';
          if (questionKey === 'testator_last_name') responseMap[question.id] = matterData.client?.last_name || '';
        });

        setResponses(responseMap);
        setTimeout(() => {
          autosaveReadyRef.current = true;
        }, 0);

      } catch (err) {
        console.error('IntakePage Load Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIntakeData();
  }, [matterId, profile]);

  const visibleQuestions = useMemo(() => {
    return questions.filter((q) => isQuestionVisible(q, responses, questions));
  }, [questions, responses]);

  const visibleSections = useMemo(() => {
    return sections.filter((section) => visibleQuestions.some((q) => q.section_id === section.id));
  }, [sections, visibleQuestions]);

  useEffect(() => {
    if (visibleSections.length === 0) return;
    if (currentSectionIndex >= visibleSections.length) {
      setCurrentSectionIndex(visibleSections.length - 1);
    }
  }, [currentSectionIndex, visibleSections.length]);

  const calculateCompletion = useCallback(() => {
    if (visibleQuestions.length === 0) return 0;
    const answeredCount = visibleQuestions.filter(q => {
      const val = responses[q.id];
      return !isEmptyResponse(val);
    }).length;
    return Math.round((answeredCount / visibleQuestions.length) * 100);
  }, [responses, visibleQuestions]);

  const buildEstatePlanDecisionPayload = useCallback(() => {
    if (!matterId) return null;

    const answerByKey = {};
    questions.forEach((question) => {
      const questionKey = getQuestionKey(question);
      if (!questionKey) return;
      answerByKey[questionKey] = responses[question.id];
    });

    const payload = {
      matter_id: matterId,
      law_firm_id: matter?.law_firm_id || null,
      updated_by: profile?.id || null
    };

    Object.entries(answerByKey).forEach(([questionKey, value]) => {
      const decisionKey = ESTATE_DECISION_COLUMNS.has(questionKey)
        ? questionKey
        : INTAKE_TO_DECISION_KEYS[questionKey];

      if (!decisionKey) return;

      const normalized = normalizeDecisionValue(decisionKey, value);
      if (normalized !== undefined && normalized !== null) {
        payload[decisionKey] = normalized;
      }
    });

    const testatorName = composeName({
      first_name: payload.testator_first_name,
      middle_initial: payload.testator_middle_initial,
      last_name: payload.testator_last_name
    });

    if (testatorName) {
      payload.testator_name = testatorName;
    }

    const spouseName = composeName({
      first_name: payload.spouse_first_name,
      middle_initial: payload.spouse_middle_initial,
      last_name: payload.spouse_last_name
    });

    if (spouseName) {
      payload.spouse_name = spouseName;
    }

    if (Array.isArray(payload.children) && payload.children.length > 0) {
      payload.child_count = payload.children.length;
    }

    if (payload.has_specific_gifts === false) {
      payload.specific_gifts = [];
      payload.specific_gift_alternate_plan = null;
      payload.tangible_personal_property_plan = null;
      payload.uses_tangible_property_written_statement = false;
      payload.pours_tangible_property_to_trust = false;
    }

    if (!payload.testator_name && matter?.client) {
      payload.testator_name = [matter.client.first_name, matter.client.last_name].filter(Boolean).join(' ');
    }

    return Object.keys(payload).length > 3 ? payload : null;
  }, [matter, matterId, profile?.id, questions, responses]);

  const saveEstatePlanDecisions = useCallback(async () => {
    const payload = buildEstatePlanDecisionPayload();
    if (!payload) return;

    const { error: decisionErr } = await supabase
      .from('estate_plan_decisions')
      .upsert(payload, { onConflict: 'matter_id' });

    if (decisionErr) throw decisionErr;
  }, [buildEstatePlanDecisionPayload]);

  const saveSectionResponses = useCallback(async (sectionId) => {
    if (!matterId || !profile?.id) return;
    
    setSaving(true);
    try {
      const sectionQuestions = visibleQuestions.filter(q => q.section_id === sectionId);
      const upserts = sectionQuestions.map(q => ({
        matter_id: matterId,
        question_id: q.id,
        question_key: getQuestionKey(q),
        template_variable_key: getTemplateVariableKey(q),
        response_json: { value: isEmptyResponse(responses[q.id]) ? null : responses[q.id] },
        updated_by: profile.id
      }));

      if (upserts.length > 0) {
        const { error: respErr } = await supabase
          .from('matter_intake_responses')
          .upsert(upserts, { onConflict: 'matter_id,question_id' });
        if (respErr) throw respErr;
      }

      const completion = calculateCompletion();
      const { error: progErr } = await supabase
        .from('matter_intake_progress')
        .upsert({
          matter_id: matterId,
          current_section_id: sectionId,
          questionnaire_id: sections.find((section) => section.id === sectionId)?.questionnaire_id || null,
          status: 'in_progress',
          completion_percent: completion,
          updated_at: new Date().toISOString()
        }, { onConflict: 'matter_id' });

      if (progErr) throw progErr;

      await saveEstatePlanDecisions();

      setProgress(prev => ({ 
        ...prev, 
        completion_percent: completion,
        current_section_id: sectionId 
      }));

    } catch (err) {
      console.error('Save Error:', err);
      setErrorQuestionIds([]);
      setError("Failed to save progress: " + err.message);
    } finally {
      setSaving(false);
    }
  }, [calculateCompletion, matterId, profile?.id, responses, saveEstatePlanDecisions, sections, visibleQuestions]);

  useEffect(() => {
    const currentSection = visibleSections[currentSectionIndex];

    if (loading || !currentSection?.id || !autosaveReadyRef.current) return;

    const sectionQuestions = questions.filter(q => q.section_id === currentSection.id);
    const autosaveKey = JSON.stringify({
      sectionId: currentSection.id,
      responses: sectionQuestions.map(q => [q.id, responses[q.id] ?? null])
    });

    if (autosaveKey === lastAutosaveKeyRef.current) return;

    const timer = setTimeout(() => {
      lastAutosaveKeyRef.current = autosaveKey;
      saveSectionResponses(currentSection.id);
    }, 1200);

    return () => clearTimeout(timer);
  }, [currentSectionIndex, loading, questions, responses, saveSectionResponses, visibleSections]);

  const handleNext = async () => {
    const currentSection = visibleSections[currentSectionIndex];
    setError(null);
    setErrorQuestionIds([]);
    await saveSectionResponses(currentSection.id);
    if (currentSectionIndex < visibleSections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setError(null);
    setErrorQuestionIds([]);
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    const currentSection = visibleSections[currentSectionIndex];
    await saveSectionResponses(currentSection.id);

    // Validate Required using the 'required' column from your SQL
    const missingRequired = visibleQuestions.filter(q => (q.is_required || q.required) && isEmptyResponse(responses[q.id]));
    if (missingRequired.length > 0) {
      const missingIds = missingRequired.map((q) => q.id);
      setErrorQuestionIds(missingIds);
      setError(`Please complete the highlighted required field${missingRequired.length === 1 ? '' : 's'} before submitting.`);

      const firstMissing = missingRequired[0];
      const firstMissingSectionIndex = visibleSections.findIndex((section) => section.id === firstMissing.section_id);
      if (firstMissingSectionIndex !== -1 && firstMissingSectionIndex !== currentSectionIndex) {
        setCurrentSectionIndex(firstMissingSectionIndex);
        setTimeout(() => {
          questionRefsRef.current[firstMissing.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        questionRefsRef.current[firstMissing.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setErrorQuestionIds([]);
      if (isClient) {
        const { data, error: submitErr } = await supabase.rpc('submit_client_intake', {
          p_matter_id: matterId
        });

        if (submitErr) throw submitErr;

        const result = Array.isArray(data) ? data[0] : data;
        navigate(result?.route_target || '/dashboard');
        return;
      }

      const { error: finalErr } = await supabase
        .from('matter_intake_progress')
        .update({
          status: 'submitted',
          completion_percent: 100,
          submitted_at: new Date().toISOString()
        })
        .eq('matter_id', matterId);

      if (finalErr) throw finalErr;

      const { error: matterStatusErr } = await supabase
        .from('matters')
        .update({ status: 'intake_submitted' })
        .eq('id', matterId);

      if (matterStatusErr) throw matterStatusErr;

      await supabase.rpc('create_intake_snapshot', {
        p_matter_id: matterId
      });

      navigate(`/matters/${matterId}`);
    } catch (err) {
      setErrorQuestionIds([]);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (questionId, value) => {
    if (errorQuestionIds.includes(questionId) && !isEmptyResponse(value)) {
      setErrorQuestionIds((prev) => prev.filter((id) => id !== questionId));
    }
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelectChange = (questionId, optionValue, checked) => {
    setResponses((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      return {
        ...prev,
        [questionId]: checked
          ? [...new Set([...current, optionValue])]
          : current.filter((item) => item !== optionValue)
      };
    });
  };

  const handlePersonArrayChange = (questionId, index, field, value) => {
    if (errorQuestionIds.includes(questionId) && !isEmptyResponse(value)) {
      setErrorQuestionIds((prev) => prev.filter((id) => id !== questionId));
    }
    setResponses((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const next = [...current];
      next[index] = {
        ...(next[index] || {}),
        [field]: value
      };
      return { ...prev, [questionId]: next };
    });
  };

  const addPersonRow = (questionId) => {
    setResponses((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      return {
        ...prev,
        [questionId]: [
          ...current,
          { first_name: '', middle_initial: '', last_name: '', date_of_birth: '', relationship: 'child' }
        ]
      };
    });
  };

  const removePersonRow = (questionId, index) => {
    setResponses((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      return {
        ...prev,
        [questionId]: current.filter((_, itemIndex) => itemIndex !== index)
      };
    });
  };

  const renderPersonListInput = (q) => {
    const people = Array.isArray(responses[q.id]) && responses[q.id].length > 0
      ? responses[q.id]
      : [{ first_name: '', middle_initial: '', last_name: '', date_of_birth: '', relationship: 'child' }];

    return (
      <div className="space-y-4">
        {people.map((person, index) => (
          <div key={`${q.id}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_110px_1fr_160px_auto] md:items-end">
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">First</span>
                <input
                  type="text"
                  value={person.first_name || ''}
                  onChange={(e) => handlePersonArrayChange(q.id, index, 'first_name', e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Middle Initial</span>
                <input
                  type="text"
                  maxLength={1}
                  value={person.middle_initial || ''}
                  onChange={(e) => handlePersonArrayChange(q.id, index, 'middle_initial', e.target.value.toUpperCase())}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last</span>
                <input
                  type="text"
                  value={person.last_name || ''}
                  onChange={(e) => handlePersonArrayChange(q.id, index, 'last_name', e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date of Birth</span>
                <input
                  type="date"
                  value={person.date_of_birth || ''}
                  onChange={(e) => handlePersonArrayChange(q.id, index, 'date_of_birth', e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </label>
              <button
                type="button"
                onClick={() => removePersonRow(q.id, index)}
                disabled={people.length === 1}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Remove child"
              >
                <SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addPersonRow(q.id)}
          className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
          Add Child
        </button>
      </div>
    );
  };

  const renderQuestionInput = (q) => {
    const value = responses[q.id];
    const options = getQuestionOptions(q);
    const questionKey = getQuestionKey(q);

    if (questionKey === 'children') {
      return renderPersonListInput(q);
    }

    if (['text', 'email', 'phone', 'number'].includes(q.question_type)) {
      const inputType = q.question_type === 'phone' ? 'tel' : q.question_type;
      return (
        <input
          type={inputType}
          value={value || ''}
          onChange={(e) => handleInputChange(q.id, e.target.value)}
          placeholder="Enter response..."
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
        />
      );
    }

    if (q.question_type === 'textarea') {
      return (
        <textarea
          rows={4}
          value={value || ''}
          onChange={(e) => handleInputChange(q.id, e.target.value)}
          placeholder="Type details here..."
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-y min-h-28"
        />
      );
    }

    if (q.question_type === 'date') {
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => handleInputChange(q.id, e.target.value)}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
        />
      );
    }

    if (q.question_type === 'select') {
      return (
        <select
          value={value || ''}
          onChange={(e) => handleInputChange(q.id, e.target.value)}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
        >
          <option value="">Select an option...</option>
          {options.map((opt) => (
            <option key={getOptionValue(opt)} value={getOptionValue(opt)}>
              {getOptionLabel(opt)}
            </option>
          ))}
        </select>
      );
    }

    if (q.question_type === 'multiselect' || q.question_type === 'checkbox') {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {options.map((opt) => {
            const optionValue = getOptionValue(opt);
            return (
              <label key={optionValue} className="flex items-center px-4 py-2 border border-slate-200 rounded-lg bg-white hover:border-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(optionValue)}
                  onChange={(e) => handleMultiSelectChange(q.id, optionValue, e.target.checked)}
                  className="mr-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">{getOptionLabel(opt)}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (q.question_type === 'yes_no' || q.question_type === 'radio') {
      const radioOptions = options.length > 0 ? options : ['Yes', 'No', 'Unsure'];
      return (
        <div className="flex flex-wrap gap-3">
          {radioOptions.map((opt) => {
            const optionValue = getOptionValue(opt);
            return (
              <label key={optionValue} className={`flex items-center px-4 py-2 border rounded-lg cursor-pointer transition-all ${value === optionValue ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="radio"
                  name={q.id}
                  value={optionValue}
                  checked={value === optionValue}
                  onChange={() => handleInputChange(q.id, optionValue)}
                  className="sr-only"
                />
                <span className={`text-sm font-medium ${value === optionValue ? 'text-blue-700' : 'text-slate-600'}`}>{getOptionLabel(opt)}</span>
              </label>
            );
          })}
        </div>
      );
    }

    return (
      <textarea
        rows={3}
        value={value || ''}
        onChange={(e) => handleInputChange(q.id, e.target.value)}
        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-y"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading questionnaire...</p>
      </div>
    );
  }

  const currentSection = visibleSections[currentSectionIndex];
  const sectionQuestions = visibleQuestions.filter(q => q.section_id === currentSection?.id);
  const isLastSection = currentSectionIndex === visibleSections.length - 1;
  const renderNavigationControls = (placement = 'bottom') => (
    <div className={`${placement === 'top' ? 'border-b' : 'border-t'} border-slate-100 bg-slate-50/50 px-8 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>
      <button
        onClick={handlePrevious}
        disabled={currentSectionIndex === 0 || saving}
        className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-30 transition-colors"
      >
        <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2 h-4 w-4" /> Previous
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <button
          onClick={() => saveSectionResponses(currentSection.id)}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 shadow-sm transition-all active:scale-95"
        >
          Save Progress
        </button>

        {!isLastSection ? (
          <button
            onClick={handleNext}
            disabled={saving}
            className="inline-flex items-center justify-center px-6 py-2 text-sm font-semibold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-50 transition-all shadow-sm active:scale-95"
          >
            Next Section <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2 h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center justify-center px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all shadow-md active:scale-95"
          >
            Submit Intake <SafeIcon icon={FiIcons.FiCheckCircle} className="ml-2 h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button onClick={() => navigate(backPath)} className="mr-4 p-2 rounded-full hover:bg-white text-slate-400 transition-colors">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-none">Estate Planning Intake</h1>
            <p className="text-sm text-slate-500 mt-2">Client: {matter.client?.first_name} {matter.client?.last_name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Overall Progress</span>
          <div className="w-32 bg-slate-100 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${calculateCompletion()}%` }}></div>
          </div>
          <span className="text-xs font-bold text-slate-900">{calculateCompletion()}%</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700 flex items-start shadow-sm">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 mr-2 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Form Container */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">Section {currentSectionIndex + 1} of {visibleSections.length}</span>
            {saving && (
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-1" /> Auto-saving...
              </span>
            )}
          </div>
          <h2 className="text-xl font-bold text-slate-900">{currentSection?.title}</h2>
          <p className="text-sm text-slate-500 mt-1">{currentSection?.description || 'Please provide the following details.'}</p>
        </div>
        {renderNavigationControls('top')}

        <div className="p-8 space-y-10">
          {sectionQuestions.map((q) => {
            const hasQuestionError = errorQuestionIds.includes(q.id);
            const questionKey = getQuestionKey(q);
            const questionEducationTopics = educationByQuestion[questionKey] || [];
            return (
            <div
              key={q.id}
              ref={(node) => {
                if (node) questionRefsRef.current[q.id] = node;
              }}
              className={`space-y-3 group rounded-lg border p-4 transition-colors ${hasQuestionError ? 'border-red-300 bg-red-50/60' : 'border-transparent'}`}
            >
              <div className="flex items-start gap-2">
                <label className="block text-sm font-semibold text-slate-800">
                  {q.question_text} {(q.is_required || q.required) && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <QuestionHelpButton
                  question={q}
                  topics={questionEducationTopics}
                  questionKey={questionKey}
                  matterId={matterId}
                />
              </div>
              {hasQuestionError && (
                <p className="flex items-start text-sm text-red-700">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                  This required field needs an answer before the intake can be submitted.
                </p>
              )}

              <LearningInvitationLink
                topics={questionEducationTopics}
                questionKey={questionKey}
                matterId={matterId}
              />

              <GuidedLearningPanel
                topics={questionEducationTopics}
                questionKey={questionKey}
                matterId={matterId}
                userId={profile?.id}
              />
              
              {renderQuestionInput(q)}
            </div>
            );
          })}
        </div>

        {/* Navigation Footer */}
        {renderNavigationControls('bottom')}
      </div>

      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[10px] text-slate-400 italic">
          Disclaimer: This platform provides technology and workflow support. Legal services are provided by the participating law firm. Financial advisors do not provide legal advice through this platform.
        </p>
      </div>
    </div>
  );
};

export default IntakePage;
